import { useState } from 'react';
import { App } from 'antd';
import { InfoCircleOutlined, SearchOutlined, TeamOutlined, WarningOutlined } from '@ant-design/icons';
import { KeyRound } from 'lucide-react';

import { Tile, TileBody, TileHeader } from '../../../components/shared';
import { Button, Checkbox, Empty, Input, Skeleton } from '../../../components/ui';
import { useDieuHuong } from '../../../hooks/useDuongDan';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { soVN } from '../../../lib/dinh-dang';
import { useAuthStore } from '../../../stores/auth';
import { trangThaiCaiMfa } from '../../nguoi-dung/constants';
import { useNguoiBatBuocMfa, useTatCaNguoiDung } from '../../nguoi-dung/hooks/useNguoiDung';
import type { UserProfile } from '../../nguoi-dung/types';
import { KHOA_MFA_TO_CHUC, useLuuMucMfa } from '../hooks/useBaoMat';
import type { FormCaiDat } from '../hooks/useFormCaiDat';
import {
  batTatId,
  chenhLech,
  coNguoiMoiBiBuoc,
  epCaToChuc,
  hauQuaMfa,
  keHoachLuu,
  khoaCaToChuc,
  locNguoi,
  suyMuc,
  trangThaiTatCa,
} from '../nghiep-vu/muc-mfa';
import type { DongMfaToChuc, HauQuaMfa, MucMfa } from '../nghiep-vu/muc-mfa';
import { loiTruongCua } from '../types';

/** Từ chừng này người trở lên mới hiện ô tìm trong danh sách chọn. */
const HIEN_O_TIM_TU = 8;


/**
 * Xác thực hai lớp theo ba mức ở thẻ Đăng nhập của cổng tenant (ADR-0047):
 * không bắt buộc, người được chọn, cả tổ chức — **một** danh sách, **một** nút lưu.
 * Dòng đầu danh sách là hộp "Tất cả mọi người trong tổ chức" (mức cả tổ chức);
 * bỏ hộp đó thì người được tích là mức "người được chọn", không ai là "không".
 *
 * Mức là thứ suy ra (`suyMuc`) từ hai nguồn sự thật: khoá `auth.mfa_required`
 * của cấu hình ba tầng (ADR-0059) và tập cờ riêng `/users/mfa-required`. Lưu
 * theo `keHoachLuu`: danh sách trước, chính sách tổ chức sau.
 *
 * Khối này không đi qua thanh lưu của form cài đặt: nó ghi đúng một khoá
 * `auth.mfa_required` với `If-Match` của chính sách đang xem, nên bản nháp các
 * khoá khác của form vẫn còn. Cổng nền tảng không dùng khối này — Platform
 * Admin không có tenant trong token để gọi `/users/mfa-required`, nên vẫn sửa
 * ô `auth.mfa_required` của form.
 */
export function KhoiMucMfa({ tenantId, form, duocSua }: { tenantId: string; form: FormCaiDat; duocSua: boolean }) {
  const { t } = useT();
  const dichLoi = useLoi();
  const qDs = useNguoiBatBuocMfa();
  const dong = form.policy?.settings.find((m) => m.key === KHOA_MFA_TO_CHUC);

  let than;
  if (qDs.error && !qDs.data) {
    than = (
      <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded border border-default px-3 py-2.5">
        <span className="min-w-0 text-sm text-danger-700">
          {t('baoMat.mfa.loiTaiDanhSach', { loi: dichLoi(qDs.error) })}
        </span>
        <Button variant="outline" size="sm" onClick={() => void qDs.refetch()}>
          {t('chung.thuLai')}
        </Button>
      </div>
    );
  } else if (!qDs.data || !form.policy || !dong) {
    // Lỗi tải chính sách đã có dải riêng ở đầu màn (`LoiTaiCaiDat`).
    than = (
      <div className="space-y-3" aria-busy="true">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-3 w-full max-w-xl" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  } else {
    than = (
      <FormMucMfa
        tenantId={tenantId}
        form={form}
        dong={dong}
        phienBan={form.policy.version}
        daLuu={qDs.data.user_ids}
        duocSua={duocSua}
      />
    );
  }

  return (
    <Tile>
      <TileHeader icon={<KeyRound size={16} />} tone="primary" title={t('baoMat.mfa.tieuDe')} />
      <TileBody className="space-y-4">{than}</TileBody>
    </Tile>
  );
}

/**
 * Bản nháp sống **không** gắn `key` với dữ liệu: `null` là đi theo bản đã lưu,
 * có giá trị là người dùng đang sửa. Nhờ vậy lưu nửa chừng hay `412` rồi tải
 * lại vẫn giữ lựa chọn, còn quản trị khác vừa lưu thì bộ chọn chưa sửa đi theo.
 *
 * Bản nháp giữ tập người đã tích **kể cả khi** đang chọn "Tất cả": bỏ "Tất cả"
 * thì lựa chọn từng người hiện lại — đúng như backend giữ tập cờ riêng ở mức
 * cả tổ chức (`keHoachLuu` không gửi danh sách).
 */
function FormMucMfa({
  tenantId,
  form,
  dong,
  phienBan,
  daLuu,
  duocSua,
}: {
  tenantId: string;
  form: FormCaiDat;
  dong: DongMfaToChuc;
  /** Phiên bản chính sách tầng tenant, gửi ở `If-Match`. */
  phienBan: number;
  /** Tập cờ riêng đã lưu (`GET /users/mfa-required`). */
  daLuu: string[];
  duocSua: boolean;
}) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const { modal } = App.useApp();
  const luu = useLuuMucMfa(tenantId);
  const toiLa = useAuthStore((st) => st.user?.id);
  const nguoiDung = useTatCaNguoiDung();
  const [nhap, setNhap] = useState<{ caToChuc: boolean; daChon: Set<string> } | null>(null);
  const [dangXuatNgay, setDangXuatNgay] = useState(false);

  const toChucDaLuu = dong.value === true;
  const biEp = epCaToChuc(dong);
  const khongChonCaToChuc = khoaCaToChuc(dong);
  const mucDaLuu = suyMuc(toChucDaLuu, daLuu);
  const caToChuc = biEp || (nhap?.caToChuc ?? toChucDaLuu);
  const daChon = nhap?.daChon ?? new Set(daLuu);
  const muc = suyMuc(caToChuc, [...daChon]);

  const truoc = { muc: mucDaLuu, daChon: daLuu };
  const sau = { muc, daChon: [...daChon] };
  const tatCaId = nguoiDung.data?.map((u) => u.user_id);
  const hauQua = hauQuaMfa(truoc, sau, tatCaId);
  // "Đăng xuất ngay" chỉ có nghĩa khi có người **mới** bị buộc (ADR-0015 điểm 6).
  const moiBiBuoc = coNguoiMoiBiBuoc(truoc, sau);
  const apDungNgay = moiBiBuoc && dangXuatNgay;
  const keHoach = keHoachLuu(muc, sau.daChon, daLuu);
  const guiChinhSach = keHoach.toChuc !== toChucDaLuu;
  const coThayDoi = guiChinhSach || keHoach.danhSach !== null;
  // Bạn có trong số người vừa được chọn: "đăng xuất ngay" đăng xuất cả bạn.
  const banBiDangXuat =
    apDungNgay &&
    muc === 'nguoi-duoc-chon' &&
    Boolean(toiLa) &&
    chenhLech(mucDaLuu === 'nguoi-duoc-chon' ? daLuu : [], sau.daChon).them.includes(toiLa!);

  // `422 USER_NOT_FOUND` theo `#/user_ids/<i>`: chỉ số trỏ vào danh sách **đã gửi**.
  const daGui = luu.variables?.danhSach?.user_ids ?? [];
  const loiNguoi = luu.error?.daLuuDanhSach
    ? []
    : loiTruongCua(luu.error).flatMap((l) => {
        const m = /^#?\/user_ids\/(\d+)$/.exec(l.pointer);
        if (!m) return [];
        const id = daGui[Number(m[1])];
        const ten = nguoiDung.data?.find((u) => u.user_id === id)?.display_name ?? id ?? '';
        return [t('baoMat.mfa.loiNguoi', { ten, loi: l.detail })];
      });
  const xungDot = luu.error?.code === 'VERSION_MISMATCH';
  const loiChung = luu.error && loiNguoi.length === 0 && !xungDot ? dichLoi(luu.error) : undefined;

  const datNhap = (moi: { caToChuc: boolean; daChon: Set<string> }) => {
    setNhap(moi);
    if (luu.error) luu.reset();
  };

  const boThayDoi = () => {
    setNhap(null);
    setDangXuatNgay(false);
    luu.reset();
  };

  const taiLai = () => {
    luu.reset();
    form.taiLai();
  };

  const xacNhanLuu = () => {
    const cauHauQua = hauQua.map((h) => cauCuaHauQua(h, apDungNgay, t, locale));
    if (banBiDangXuat) cauHauQua.push(t('baoMat.hauQua.banBiDangXuat'));
    const nutDangXuat =
      muc === 'nguoi-duoc-chon' ? 'baoMat.mfa.nutLuuVaDangXuatNguoiChon' : 'baoMat.mfa.nutLuuVaDangXuat';
    modal.confirm({
      icon: <WarningOutlined className="text-warning-600" />,
      title: t('baoMat.xacNhan.tieuDe'),
      content: (
        <div className="space-y-2 text-sm text-content-secondary">
          <ul className="list-disc space-y-1 pl-5">
            {cauHauQua.map((cau) => (
              <li key={cau}>{cau}</li>
            ))}
          </ul>
          <p>{t(apDungNgay ? 'baoMat.xacNhan.khongHoanTac' : 'baoMat.xacNhan.hieuLucNgay')}</p>
        </div>
      ),
      okText: t(apDungNgay ? nutDangXuat : 'baoMat.mfa.nutLuu'),
      okButtonProps: { danger: apDungNgay },
      cancelText: t('chung.huy'),
      // Lỗi hiện cạnh danh sách hoặc cạnh nút lưu, nên hộp đóng lại dù lưu hỏng.
      onOk: () =>
        luu
          .mutateAsync({
            danhSach: keHoach.danhSach && { user_ids: keHoach.danhSach, apply_now: apDungNgay },
            chinhSach: guiChinhSach
              ? { ifMatch: phienBan, toChuc: keHoach.toChuc, apDungNgay: apDungNgay && keHoach.toChuc }
              : null,
          })
          .then(() => {
            setNhap(null);
            setDangXuatNgay(false);
          })
          .catch(() => undefined),
    });
  };

  return (
    <>
      <ChonNguoi
        ds={nguoiDung.data}
        loiTai={nguoiDung.error}
        onThuLai={() => void nguoiDung.refetch()}
        muc={muc}
        caToChuc={caToChuc}
        daChon={daChon}
        toiLa={toiLa}
        duocSua={duocSua && !biEp}
        // Bị ép thì "Tất cả" tích sẵn và khoá, kèm lý do; khoá ở tầng tenant thì không chọn được.
        lyDoKhoaTatCa={
          dong.forced_by
            ? t('baoMat.mfa.caToChucBiEpEmail')
            : biEp
              ? t('baoMat.mfa.caToChucBiEp')
              : khongChonCaToChuc
                ? t('baoMat.mfa.caToChucKhongSua')
                : undefined
        }
        loiNguoi={loiNguoi}
        onTatCa={(bat) => datNhap({ caToChuc: bat, daChon })}
        onChon={(id) => datNhap({ caToChuc, daChon: batTatId(daChon, id) })}
      />

      {duocSua && moiBiBuoc && (
        <div className="space-y-1 rounded border border-default bg-subtle p-3">
          <Checkbox
            checked={dangXuatNgay}
            onChange={(e) => setDangXuatNgay(e.target.checked)}
            aria-describedby="bao-mat-dang-xuat-mo"
          >
            {t(muc === 'nguoi-duoc-chon' ? 'baoMat.mfa.dangXuatNguoiChon' : 'baoMat.mfa.dangXuatNgay')}
          </Checkbox>
          <p id="bao-mat-dang-xuat-mo" className="pl-6 text-xs text-content-muted">
            {t(
              !dangXuatNgay
                ? 'baoMat.mfa.apDungDanMo'
                : muc === 'nguoi-duoc-chon'
                  ? 'baoMat.mfa.dangXuatNguoiChonMo'
                  : 'baoMat.mfa.dangXuatNgayMo',
            )}
          </p>
        </div>
      )}

      {/* Hậu quả của lựa chọn so với bản đã lưu — cùng câu với hộp xác nhận. */}
      {hauQua.length > 0 && (
        <div role="status" className="space-y-1 rounded border border-default bg-subtle px-3 py-2">
          <p className="text-xs font-medium text-content-secondary">{t('baoMat.hauQua.tieuDe')}</p>
          <ul className="list-disc space-y-0.5 pl-5 text-xs leading-relaxed text-content-muted">
            {hauQua.map((h) => {
              const cau = cauCuaHauQua(h, apDungNgay, t, locale);
              return <li key={cau}>{cau}</li>;
            })}
          </ul>
        </div>
      )}

      {/* Lưu nửa chừng: danh sách đã lưu, chính sách tổ chức thì chưa. */}
      {luu.error?.daLuuDanhSach && (
        <div role="alert" className="rounded border border-warning-200 bg-warning-50 px-3 py-2 text-sm text-warning-700">
          {t('baoMat.mfa.luuNuaChung')}
        </div>
      )}
      {xungDot && (
        <div
          role="alert"
          className="flex flex-col gap-3 rounded border border-warning-200 bg-warning-50 px-3 py-2 md:flex-row md:items-center md:justify-between"
        >
          <span className="flex min-w-0 items-start gap-2 text-sm text-warning-700">
            <WarningOutlined className="mt-0.5 shrink-0" aria-hidden />
            {t('baoMat.mfa.xungDot')}
          </span>
          <Button variant="outline" size="sm" loading={form.dangTaiLai} onClick={taiLai}>
            {t('caiDat.taiLaiBanMoi')}
          </Button>
        </div>
      )}

      {duocSua && !biEp && (
        <div className="flex flex-wrap items-center justify-end gap-3">
          {loiChung && (
            <p role="alert" className="min-w-0 flex-1 basis-full text-sm text-danger-600 sm:basis-auto">
              {loiChung}
            </p>
          )}
          <Button variant="outline" size="md" disabled={!coThayDoi || luu.isPending} onClick={boThayDoi}>
            {t('baoMat.mfa.boThayDoi')}
          </Button>
          <Button variant="primary" size="md" disabled={!coThayDoi || xungDot} loading={luu.isPending} onClick={xacNhanLuu}>
            {t(luu.isPending ? 'chung.dangLuu' : 'baoMat.mfa.nutLuu')}
          </Button>
        </div>
      )}
    </>
  );
}

type HamDich = ReturnType<typeof useT>['t'];

/** Một dòng hậu quả thành câu. Số đi theo ngôn ngữ; `null` là chưa đếm được. */
function cauCuaHauQua(h: HauQuaMfa, apDungNgay: boolean, t: HamDich, locale: string): string {
  switch (h.loai) {
    case 'batCaToChuc':
      return t(apDungNgay ? 'baoMat.xacNhan.mfaBatNgay' : 'baoMat.xacNhan.mfaBatDan');
    case 'moRongCaToChuc':
      if (h.so === null) return t(apDungNgay ? 'baoMat.hauQua.moRongNgay' : 'baoMat.hauQua.moRongDan');
      return t(apDungNgay ? 'baoMat.hauQua.moRongSoNgay' : 'baoMat.hauQua.moRongSoDan', {
        so: soVN(h.so, locale),
      });
    case 'thuHep':
      return h.thoi === null
        ? t('baoMat.hauQua.thuHep', { giu: soVN(h.giu, locale) })
        : t('baoMat.hauQua.thuHepSo', { giu: soVN(h.giu, locale), thoi: soVN(h.thoi, locale) });
    case 'tatCaToChuc':
      return t('baoMat.xacNhan.mfaTat');
    case 'themNguoi':
      return t(apDungNgay ? 'baoMat.hauQua.themNguoiNgay' : 'baoMat.hauQua.themNguoiDan', {
        so: soVN(h.so, locale),
      });
    case 'boNguoi':
      return t('baoMat.hauQua.boNguoi', { so: soVN(h.so, locale) });
  }
}

/**
 * Một danh sách cho cả ba mức: dòng đầu là hộp "Tất cả mọi người trong tổ
 * chức" (mức cả tổ chức), các dòng sau là từng người (mức người được chọn).
 * Đủ bốn trạng thái; rỗng vì ô tìm thì chỉ còn tiêu đề. Hộp "Tất cả" vẫn dùng
 * được khi danh sách người chưa tải hay tải lỗi — mức cả tổ chức không cần nó.
 *
 * Người đã chọn mà ô tìm đang che vẫn được tính trong "Đã chọn" và vẫn được lưu.
 */
function ChonNguoi({
  ds,
  loiTai,
  onThuLai,
  muc,
  caToChuc,
  daChon,
  toiLa,
  duocSua,
  lyDoKhoaTatCa,
  loiNguoi,
  onTatCa,
  onChon,
}: {
  ds: UserProfile[] | undefined;
  loiTai: unknown;
  onThuLai: () => void;
  muc: MucMfa;
  caToChuc: boolean;
  daChon: ReadonlySet<string>;
  toiLa: string | undefined;
  duocSua: boolean;
  /** Có thì hộp "Tất cả" bị khoá, câu này giải thích vì sao. */
  lyDoKhoaTatCa: string | undefined;
  /** Lỗi theo từng người server trả (`#/user_ids/<i>`), đã thành câu. */
  loiNguoi: string[];
  onTatCa: (bat: boolean) => void;
  onChon: (id: string) => void;
}) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const dieuHuong = useDieuHuong();
  const [tuKhoa, setTuKhoa] = useState('');
  const hien = ds ? locNguoi(ds, tuKhoa) : [];
  const coLoc = tuKhoa.trim() !== '';
  const tatCa = trangThaiTatCa(
    caToChuc,
    daChon,
    ds?.map((u) => u.user_id),
  );

  let than;
  if (!ds && loiTai) {
    than = (
      <div role="alert" className="flex flex-wrap items-center justify-between gap-3 px-3 py-2.5">
        <span className="min-w-0 text-sm text-danger-700">
          {t('baoMat.mfa.loiTaiNguoi', { loi: dichLoi(loiTai) })}
        </span>
        <Button variant="outline" size="sm" onClick={onThuLai}>
          {t('chung.thuLai')}
        </Button>
      </div>
    );
  } else if (!ds) {
    than = (
      <div className="space-y-3 p-3" aria-busy="true" aria-label={t('baoMat.mfa.dangTaiNguoi')}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="h-4 w-4" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-3 w-1/3" />
            </div>
          </div>
        ))}
      </div>
    );
  } else if (ds.length === 0) {
    than = (
      <Empty
        className="py-8"
        icon={TeamOutlined}
        title={t('baoMat.mfa.rongNguoi')}
        description={t('baoMat.mfa.rongNguoiMo')}
        action={
          <Button variant="outline" size="md" onClick={() => dieuHuong('/he-thong/nguoi-dung')}>
            {t('baoMat.mfa.denCauHinhNguoiDung')}
          </Button>
        }
      />
    );
  } else if (hien.length === 0) {
    than = <Empty className="py-8" icon={SearchOutlined} title={t('baoMat.mfa.rongLoc', { tuKhoa: tuKhoa.trim() })} />;
  } else {
    than = (
      <ul className="max-h-80 divide-y divide-default overflow-y-auto">
        {hien.map((u) => {
          const chon = caToChuc || daChon.has(u.user_id);
          const cai = trangThaiCaiMfa(u.mfa_configured, chon);
          return (
            <li key={u.user_id} className="flex items-start gap-3 px-3 py-2">
              <Checkbox
                className="min-w-0 flex-1"
                checked={chon}
                disabled={!duocSua || caToChuc}
                onChange={() => onChon(u.user_id)}
              >
                {/* Nhãn của antd Checkbox không co được, nên để tên dài xuống dòng thay vì `truncate`. */}
                <span className="flex min-w-0 flex-col">
                  <span className="break-words text-sm text-content-primary">
                    {u.display_name}
                    {u.user_id === toiLa && (
                      <span className="ml-1.5 text-2xs text-content-muted">{t('nguoiDung.ban')}</span>
                    )}
                  </span>
                  <span className="break-all text-2xs text-content-muted">
                    {u.email ?? t('nguoiDung.chuaDocDuocEmail')}
                  </span>
                </span>
              </Checkbox>
              <span className={`mt-0.5 shrink-0 text-2xs ${cai.lop}`}>{t(cai.khoa)}</span>
            </li>
          );
        })}
      </ul>
    );
  }

  const tomTat =
    muc === 'ca-to-chuc'
      ? t('baoMat.mfa.tomTatCaToChuc')
      : muc === 'nguoi-duoc-chon'
        ? t('baoMat.mfa.tomTatNguoi', { so: soVN(daChon.size, locale) })
        : t('baoMat.mfa.tomTatKhong');

  return (
    <fieldset className="min-w-0 space-y-3">
      <legend className="mb-1 text-sm font-medium text-content-primary">{t('baoMat.mfa.mucNhan')}</legend>

      {/* Kết quả hiệu lực của lựa chọn đang xem — đọc lại khi đổi. */}
      <div role="status" className="space-y-0.5">
        <p className="text-sm font-medium text-content-primary">{tomTat}</p>
        {/* Một câu hậu quả, chỉ khi có người bị bắt buộc: tóm tắt phía trên đã nói ai. */}
        {muc !== 'khong' && <p className="text-sm leading-relaxed text-content-muted">{t('baoMat.mfa.chuaCaiUngDung')}</p>}
      </div>

      {lyDoKhoaTatCa && (
        <p
          id="bao-mat-mfa-ly-do-khoa"
          className="rounded border border-default bg-subtle px-3 py-2 text-sm leading-relaxed text-content-secondary"
        >
          {lyDoKhoaTatCa}
        </p>
      )}

      {ds && ds.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          {ds.length >= HIEN_O_TIM_TU ? (
            <Input
              allowClear
              className="w-full sm:w-72"
              prefix={<SearchOutlined className="text-content-muted" />}
              placeholder={t('baoMat.mfa.timPlaceholder')}
              aria-label={t('baoMat.mfa.timNhan')}
              aria-controls="bao-mat-mfa-ds"
              value={tuKhoa}
              onChange={(e) => setTuKhoa(e.target.value)}
            />
          ) : (
            <span />
          )}
          {!caToChuc && (
            <span className="text-xs text-content-muted">
              {coLoc
                ? t('baoMat.mfa.daChonLoc', { so: soVN(daChon.size, locale), hien: soVN(hien.length, locale) })
                : t('baoMat.mfa.daChon', { so: soVN(daChon.size, locale) })}
            </span>
          )}
        </div>
      )}

      <div className="overflow-hidden rounded border border-default">
        {/* Dòng đầu danh sách: chọn thì áp cho cả tổ chức, mọi dòng dưới tích sẵn và khoá. */}
        <div className="border-b border-default bg-subtle px-3 py-2">
          <Checkbox
            checked={tatCa.checked}
            indeterminate={tatCa.indeterminate}
            disabled={!duocSua || Boolean(lyDoKhoaTatCa)}
            aria-controls="bao-mat-mfa-ds"
            aria-describedby={lyDoKhoaTatCa ? 'bao-mat-mfa-tat-ca-mo bao-mat-mfa-ly-do-khoa' : 'bao-mat-mfa-tat-ca-mo'}
            // Đang lửng (đã tích vài người) mà bấm thì lên "Tất cả", như hộp kiểm ba trạng thái thường gặp.
            onChange={(e) => onTatCa(e.target.checked)}
          >
            <span className="flex min-w-0 flex-col">
              <span className="text-sm font-medium text-content-primary">{t('baoMat.mfa.tatCaNhan')}</span>
              <span id="bao-mat-mfa-tat-ca-mo" className="text-2xs text-content-muted">
                {t('baoMat.mfa.tatCaMo')}
              </span>
            </span>
          </Checkbox>
        </div>
        <div id="bao-mat-mfa-ds">{than}</div>
      </div>

      {tatCa.duTungNguoi && (
        <p role="status" className="flex items-start gap-2 text-xs leading-relaxed text-content-secondary">
          <InfoCircleOutlined className="mt-0.5 shrink-0 text-primary-600" aria-hidden />
          {t('baoMat.mfa.goiYChonTatCa')}
        </p>
      )}
      {loiNguoi.map((cau) => (
        <p key={cau} role="alert" className="text-xs text-danger-600">
          {cau}
        </p>
      ))}
    </fieldset>
  );
}
